import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  CircularProgress,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import AppDrawer from "../../../AppComponents/AppDrawer";
import AppStatusStepper from "../../../AppComponents/AppStatusStepper";
import ProductDetailsStepper from "./ProductDetailsStepper";
import { useGetClientDetails, useGetClientOnboardingProgress } from "../../../../hooks/useClientOnboarding";

// Styled components for ClientDetailsDrawer
const DrawerContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const CenterContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 200,
});

const MainContentLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(6),
}));

const SectionHeaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
});

const CustomListContainer = styled(List)(({ theme }) => ({
  padding: 0,
  '& > .MuiListItem-root:not(:last-child)': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const CustomListItem = styled(ListItem)(({ theme, isClickable, isSelected }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.2s ease',
  cursor: isClickable ? 'pointer' : 'default',
  '&:hover': {
    backgroundColor: isClickable ? theme.palette.action.hover : 'inherit',
  },
  backgroundColor: isSelected ? theme.palette.action.selected : 'transparent',
}));

const ClientDetailsDrawer = ({ open, onClose, params }) => {
  const { data, isLoading, error } = useGetClientDetails(open && params ? params : null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  
  // Fetch client-specific onboarding progress when a client is selected
  const { 
    data: clientProgressData, 
    isLoading: clientProgressLoading, 
    error: clientProgressError 
  } = useGetClientOnboardingProgress(selectedClientId);

  const family = data?.data?.family;
  const selectedClient = data?.data?.selectedClient;
  const clients = data?.data?.clients || [];
  const productDetails = data?.data?.productDetails || [];

  // Get the current client (only when explicitly selected)
  const currentClient = selectedClientId 
    ? clients.find(client => client.id === selectedClientId)
    : null;

  const filteredProductDetails = currentClient
    ? productDetails.filter(pd => pd.clientId === currentClient.customerClientNameId || pd.clientId === currentClient.id)
    : productDetails;

  // Stepper data for products with proper memoization and deduplication
  const productStepperData = React.useMemo(() => {
    // Helper function to normalize task status
    const normalizeTaskStatus = (status) => {
      const normalizedStatus = status?.toLowerCase();
      switch (normalizedStatus) {
        case 'completed':
          return 'completed';
        case 'inprogress':
        case 'in_progress':
          return 'in_progress';
        case 'todo':
        case 'pending':
        default:
          return 'todo';
      }
    };

    if (currentClient && clientProgressData) {
      // When a specific client is selected and we have client progress data, use that data
      return (clientProgressData.products || []).map(product => ({
        name: product.productName,
        steps: product.tasks ? product.tasks.map(task => normalizeTaskStatus(task.status)) : []
      }));
    } else if (currentClient) {
      // Fallback to filtered product details if no client progress data
      return filteredProductDetails.flatMap(detail =>
        (detail.products || []).map(product => ({
          name: product.productName,
          steps: product.tasks ? product.tasks.map(task => normalizeTaskStatus(task.status)) : []
        }))
      );
    } else {
      // When no client is selected, show all products (allowing duplicates)
      return productDetails.flatMap(detail =>
        (detail.products || []).map(product => ({
          name: product.productName,
          steps: product.tasks ? product.tasks.map(task => normalizeTaskStatus(task.status)) : []
        }))
      );
    }
  }, [currentClient, clientProgressData, filteredProductDetails, productDetails]);

  // Task status data for the current client
  const currentClientTasks = currentClient?.tasks || selectedClient?.tasks || [];
  
  // Convert tasks array to steps format for AppStatusStepper
  const taskSteps = currentClientTasks.map(task => ({ 
    id: task.id, 
    label: task.taskName 
  }));
  
  // Convert tasks array to status mapping for AppStatusStepper
  const taskStatusMapping = currentClientTasks.reduce((acc, task) => {
    let normalizedStatus = task.status?.toLowerCase();
    
    // Map API status values to stepper expected values
    switch (normalizedStatus) {
      case 'completed':
        acc[task.id] = 'completed';
        break;
      case 'inprogress':
      case 'in_progress':
        acc[task.id] = 'in_progress';
        break;
      case 'todo':
      case 'pending':
      default:
        acc[task.id] = 'todo';
        break;
    }
    
    return acc;
  }, {});
  
  // Handler to reset selection and close drawer
  const handleDrawerClose = useCallback(() => {
    setSelectedClientId(null);
    onClose();
  }, [onClose]);

  // Handler for client selection
  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
  };

  // Get client name for display
  const getClientDisplayName = (client) => {
    // If we have client progress data for this client, use that name
    if (client.id === selectedClientId && clientProgressData?.clientName) {
      return clientProgressData.clientName;
    }
    return client.name || 'Unknown Client';
  };

  // Handler to clear client selection
  const handleClearSelection = () => {
    setSelectedClientId(null);
  };

  return (
    <AppDrawer
      open={open}
      onClose={handleDrawerClose}
      title={family?.familyName ? `${family.familyName} - ${selectedClient?.productName}` : "Client Details"}
      width="40%"
    >
      <DrawerContentContainer>
        {isLoading || (selectedClientId && clientProgressLoading) ? (
          <CenterContainer>
            <CircularProgress />
          </CenterContainer>
        ) : error || (selectedClientId && clientProgressError) ? (
          <Typography color="error">
            {error?.message || clientProgressError?.message || "Failed to load data."}
          </Typography>
        ) : data ? (
          <MainContentLayout>
            {/* Task Status Section */}
            <Box sx={{ pb: 2 }}>
              <Typography
                variant="h6"
                sx={{ color: (theme) => theme.palette.text.primary, fontSize: "16px", fontWeight: 500, mb: 3 }}
              >
                Task Status
              </Typography>
              <AppStatusStepper
                steps={taskSteps}
                stepsStatus={taskStatusMapping}
              />
            </Box>

            {/* Clients Section */}
            {clients.length > 0 && (
              <Box>
                <SectionHeaderContainer>
                  <Typography
                    variant="h6"
                    sx={{ color: (theme) => theme.palette.text.primary, fontSize: "16px", fontWeight: 500 }}
                  >
                    Clients
                  </Typography>
                  {selectedClientId && clients.length > 1 && (
                    <Typography
                      variant="body2"
                      onClick={handleClearSelection}
                      sx={{
                        color: (theme) => theme.palette.custom.brown.main,
                        cursor: "pointer",
                        textDecoration: "underline",
                        "&:hover": { opacity: 0.8 }
                      }}
                    >
                      Clear Selection
                    </Typography>
                  )}
                </SectionHeaderContainer>
                <CustomListContainer>
                  {clients.map((client, index) => (
                    <CustomListItem
                      key={client.id}
                      onClick={() => handleClientSelect(client.id)}
                      isClickable={true}
                      isSelected={selectedClientId === client.id}
                    >
                      <Typography sx={{
                        color: (theme) => theme.palette.text.primary,
                        fontSize: "14px",
                        fontWeight: selectedClientId === client.id ? 600 : 400,
                        display: "flex",
                        alignItems: "center"
                      }}>
                        {`${index + 1}. ${getClientDisplayName(client)}`}
                      </Typography>
                    </CustomListItem>
                  ))}
                </CustomListContainer>
              </Box>
            )}

            {/* Product Details Section */}
            <Box>
              <Typography
                variant="h6"
                sx={{ color: (theme) => theme.palette.text.primary, fontSize: "16px", fontWeight: 500, mb: 3 }}
              >
                Product Details
              </Typography>
              <ProductDetailsStepper steps={productStepperData} />
            </Box>
          </MainContentLayout>
        ) : (
          <Typography>No data available.</Typography>
        )}
      </DrawerContentContainer>
    </AppDrawer>
  );
};

export default ClientDetailsDrawer;